import { Button, Collapse, FilePreview, makeStyles, Typography, useMediaQuery } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { FeatureProduct } from '@paintscout/util/builder'
import { getQuoteFeatureProducts } from '@paintscout/util/builder'
import React, { useState } from 'react'
import { useQuote } from '../context'
import HideAmountButton from './HideAmountButton'
import OutlinedTableCard from './OutlinedTableCard'

const paintCanSize = 72

const SW_LOGO = 'https://res.cloudinary.com/taptapquote/image/upload/v1751660780/paintscout/s-w-logo.png'
const SW_LOGO_XS = 'https://paintscout.s3.us-east-1.amazonaws.com/partners/sw/images/s-w-icon.png'

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        '@media print': {
          display: 'none'
        }
      },
      // The provider sub-header ("Sherwin-Williams" + logo) — a lighter band under the card header.
      groupHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        minHeight: 44,
        padding: theme.spacing(0.5, 2),
        borderBottom: `1px solid ${neutral.P90}`
      },
      groupLabel: {
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P35
      },
      logo: {
        height: 28,
        objectFit: 'contain',
        flexShrink: 0
      },
      row: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(3),
        padding: theme.spacing(2.5, 2),
        '&:not(:last-child)': {
          borderBottom: `1px solid ${neutral.P90}`
        },
        [theme.breakpoints.down('xs')]: {
          gap: theme.spacing(2)
        }
      },
      filePreview: {
        maxWidth: paintCanSize,
        minWidth: paintCanSize,
        minHeight: paintCanSize,
        flexShrink: 0
      },
      productDetails: {
        minWidth: 0
      },
      productLabel: {
        fontWeight: 600,
        color: neutral.P35,
        marginBottom: theme.spacing(1)
      },
      description: {
        color: neutral.P50,
        whiteSpace: 'pre-wrap'
      },
      viewMore: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(1),
        borderTop: `1px solid ${neutral.P90}`
      }
    }
  },
  { name: 'ProductsTableNext' }
)

export interface ProductsTableNextProps {
  collapsed?: boolean
  inPresentation?: boolean
  onToggle: (ev: React.MouseEvent) => any
}

/**
 * The "Products" (feature products) section for the new (estimateNext) pipeline. A fresh design built
 * on the shared `OutlinedTableCard`: a "Products" header with the Hide/Show pill, a "Sherwin-Williams"
 * provider sub-header (with the SW logo), then one row per product (paint-can thumbnail + name +
 * description). Reads the same `getQuoteFeatureProducts` data as the legacy `ProductsTable`, which is
 * left untouched for the classic view.
 */
export default function ProductsTableNext({ collapsed, inPresentation, onToggle }: ProductsTableNextProps) {
  const classes = useStyles()
  const { quote, isEditable } = useQuote()
  const { options } = useClientOptions()
  const isXs = useMediaQuery('xs')

  const items = getQuoteFeatureProducts({ quote, options })
  const show = !collapsed && items.length > 0

  const [showAll, setShowAll] = useState(false)
  const visibleItems = showAll ? items : items.slice(0, 3)

  // In presentation/customer contexts a collapsed section is simply gone; the editor keeps the header
  // so the Hide/Show toggle stays reachable.
  if (inPresentation && !show) {
    return null
  }

  return (
    <div className={classes.root}>
      <OutlinedTableCard
        title={'Products'}
        headerVariant={'light'}
        singleBorder
        // When there's nothing to show (collapsed or no products), drop the header's bottom divider so
        // it doesn't leave a stray border stacked on the card's bottom edge.
        hideHeaderDivider={!show}
        headerRight={
          // Nothing to hide until a product is listed (ENG-1478).
          isEditable && !inPresentation && items.length > 0 ? (
            <HideAmountButton visible={!collapsed} visibleLabel="Hide" hiddenLabel="Show" onToggle={onToggle} />
          ) : undefined
        }
      >
        <Collapse show={show}>
          <div className={classes.groupHeader}>
            <Typography component="span" className={classes.groupLabel}>
              Sherwin-Williams
            </Typography>
            <img className={classes.logo} src={isXs ? SW_LOGO_XS : SW_LOGO} alt={'Sherwin-Williams'} />
          </div>

          {visibleItems.map((item: FeatureProduct, index) => (
            <div className={classes.row} key={item.key ?? index}>
              <FilePreview
                classes={{ root: classes.filePreview }}
                file={{
                  ...item.featureImage,
                  src: item?.featureImage?.value ?? null,
                  // Partner feature-product images live in the legacy `paintscout` S3 bucket, which
                  // returns no Access-Control-Allow-Origin. Flagging them provider-hosted opts them out
                  // of FilePreview's crossOrigin load so they don't render blank (see legacy ProductsTable).
                  provider: 'sherwin-williams',
                  visibility: 'visible'
                }}
              />
              <div className={classes.productDetails}>
                <Typography variant="h3" className={classes.productLabel}>
                  {item.label}
                </Typography>
                <Typography variant="body1" className={classes.description}>
                  {item.description}
                </Typography>
              </div>
            </div>
          ))}

          {items.length > 3 && (
            <div className={classes.viewMore}>
              <Button variant={'text'} onClick={() => setShowAll((prev) => !prev)}>
                {showAll ? 'View Less' : 'View More'}
              </Button>
            </div>
          )}
        </Collapse>
      </OutlinedTableCard>
    </div>
  )
}
